import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ShieldAlert, Ban, CheckCircle2, LogOut, Users, Activity, Globe, Zap, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast/headless'
import { useAuthStore } from '../../hooks/useAuthStore'
import {
  fetchOnlineUids,
  fetchUsers,
  fetchActivity,
  banUser,
  kickUser,
  unbanUser,
  fetchBannedIps,
  banIp,
  unbanIp
} from '../../api/admin'

const ONLINE_POLL_MS = 5000

const TABS = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'banned-ips', label: 'Banned IPs', icon: Globe }
]

function formatDate(value) {
  if (!value) return '—'
  const date = value._seconds ? new Date(value._seconds * 1000) : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

function BanDialog({ target, onCancel, onConfirm }) {
  const [reason, setReason] = useState('')
  const [alsoBanIp, setAlsoBanIp] = useState(false)

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-effect rounded-xl p-6 w-full max-w-sm"
      >
        <h3 className="text-white font-semibold text-lg mb-4">
          Ban {target.username || target.id}?
        </h3>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)"
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
          rows={2}
        />
        <label className="flex items-center gap-2 text-sm text-gray-300 mb-4">
          <input
            type="checkbox"
            checked={alsoBanIp}
            onChange={(e) => setAlsoBanIp(e.target.checked)}
          />
          Also ban their last known IP ({target.lastIp || 'unknown'})
        </label>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-gray-300 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ reason, alsoBanIp })}
            className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
          >
            Ban
          </button>
        </div>
      </motion.div>
    </div>
  )
}

function UsersTab() {
  const [users, setUsers] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [banTarget, setBanTarget] = useState(null)
  const [onlineUids, setOnlineUids] = useState(() => new Set())

  const load = useCallback(async (nextCursor = null) => {
    setLoading(true)
    try {
      const data = await fetchUsers({ cursor: nextCursor })
      setUsers((prev) => (nextCursor ? [...prev, ...data.users] : data.users))
      setCursor(data.nextCursor)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Separate from `load()`/pagination — polls just the connected-uid set so
  // the online indicator stays live without disturbing loaded pages.
  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const uids = await fetchOnlineUids()
        if (!cancelled) setOnlineUids(new Set(uids))
      } catch {
        // transient — next poll will retry
      }
    }
    poll()
    const interval = setInterval(poll, ONLINE_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  const handleBan = async ({ reason, alsoBanIp }) => {
    await banUser(banTarget.id, { reason, alsoBanIp })
    setBanTarget(null)
    load()
  }

  const handleUnban = async (uid) => {
    await unbanUser(uid)
    load()
  }

  const handleKick = async (uid) => {
    const { wasConnected } = await kickUser(uid)
    toast(wasConnected ? 'User disconnected' : 'User is not currently connected')
  }

  // Online users float to the top; sort is stable so each group otherwise
  // keeps the createdAt-desc order the API returned.
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => (onlineUids.has(b.id) ? 1 : 0) - (onlineUids.has(a.id) ? 1 : 0))
  }, [users, onlineUids])

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-300">
          <thead className="text-xs uppercase text-gray-400 border-b border-gray-700">
            <tr>
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Type</th>
              <th className="py-2 pr-4">Online</th>
              <th className="py-2 pr-4">Last IP</th>
              <th className="py-2 pr-4">Last Active</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((u) => (
              <tr key={u.id} className="border-b border-gray-800">
                <td className="py-2 pr-4">
                  <div className="text-white">{u.username || 'Unnamed'}</div>
                  <div className="text-gray-500 text-xs">{u.email || u.id}</div>
                </td>
                <td className="py-2 pr-4">{u.isAnonymous ? 'Guest' : 'Registered'}</td>
                <td className="py-2 pr-4">
                  {onlineUids.has(u.id) ? (
                    <span className="flex items-center gap-1 text-green-400">
                      <span className="w-2 h-2 rounded-full bg-green-400" /> Online
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-gray-500">
                      <span className="w-2 h-2 rounded-full bg-gray-600" /> Offline
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4">{u.lastIp || '—'}</td>
                <td className="py-2 pr-4">{formatDate(u.lastActive)}</td>
                <td className="py-2 pr-4">
                  {u.banned ? (
                    <span className="text-red-400">Banned</span>
                  ) : (
                    <span className="text-green-400">Active</span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleKick(u.id)}
                      className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300"
                      title="Disconnect their live session without banning"
                    >
                      <Zap className="w-4 h-4" /> Kick
                    </button>
                    {u.banned ? (
                      <button
                        onClick={() => handleUnban(u.id)}
                        className="flex items-center gap-1 text-green-400 hover:text-green-300"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Unban
                      </button>
                    ) : (
                      <button
                        onClick={() => setBanTarget(u)}
                        className="flex items-center gap-1 text-red-400 hover:text-red-300"
                      >
                        <Ban className="w-4 h-4" /> Ban
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cursor && (
        <button
          onClick={() => load(cursor)}
          disabled={loading}
          className="mt-4 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm"
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}

      {banTarget && (
        <BanDialog
          target={banTarget}
          onCancel={() => setBanTarget(null)}
          onConfirm={handleBan}
        />
      )}
    </div>
  )
}

function ActivityTab() {
  const [entries, setEntries] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [typeFilter, setTypeFilter] = useState('')

  const load = useCallback(async (nextCursor = null, type = typeFilter) => {
    setLoading(true)
    try {
      const data = await fetchActivity({ cursor: nextCursor, type: type || undefined })
      setEntries((prev) => (nextCursor ? [...prev, ...data.entries] : data.entries))
      setCursor(data.nextCursor)
    } finally {
      setLoading(false)
    }
  }, [typeFilter])

  useEffect(() => { load(null, typeFilter) }, [typeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-sm text-gray-400">Filter:</label>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg text-white text-sm px-2 py-1"
        >
          <option value="">All</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
          <option value="game_created">Game Created</option>
          <option value="game_joined">Game Joined</option>
          <option value="game_started">Game Started</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-300">
          <thead className="text-xs uppercase text-gray-400 border-b border-gray-700">
            <tr>
              <th className="py-2 pr-4">When</th>
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Event</th>
              <th className="py-2 pr-4">IP</th>
              <th className="py-2 pr-4">Game</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-gray-800">
                <td className="py-2 pr-4">{formatDate(e.timestamp)}</td>
                <td className="py-2 pr-4">{e.username || e.uid}</td>
                <td className="py-2 pr-4">{e.type}</td>
                <td className="py-2 pr-4">{e.ip || '—'}</td>
                <td className="py-2 pr-4">{e.gameType || (e.gameId ? e.gameId.slice(0, 8) : '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cursor && (
        <button
          onClick={() => load(cursor)}
          disabled={loading}
          className="mt-4 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm"
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}

function BannedIpsTab() {
  const [ips, setIps] = useState([])
  const [newIp, setNewIp] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setIps(await fetchBannedIps())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!newIp.trim()) return
    await banIp(newIp.trim(), reason.trim() || undefined)
    setNewIp('')
    setReason('')
    load()
  }

  const handleRemove = async (ip) => {
    await unbanIp(ip)
    load()
  }

  return (
    <div>
      <form onSubmit={handleAdd} className="flex flex-wrap gap-2 mb-4">
        <input
          value={newIp}
          onChange={(e) => setNewIp(e.target.value)}
          placeholder="IP address"
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional)"
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
        />
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
        >
          Ban IP
        </button>
      </form>

      {loading ? (
        <div className="text-gray-400">Loading…</div>
      ) : (
        <table className="w-full text-sm text-left text-gray-300">
          <thead className="text-xs uppercase text-gray-400 border-b border-gray-700">
            <tr>
              <th className="py-2 pr-4">IP</th>
              <th className="py-2 pr-4">Reason</th>
              <th className="py-2 pr-4">Banned At</th>
              <th className="py-2 pr-4">Action</th>
            </tr>
          </thead>
          <tbody>
            {ips.map((entry) => (
              <tr key={entry.ip} className="border-b border-gray-800">
                <td className="py-2 pr-4 text-white">{entry.ip}</td>
                <td className="py-2 pr-4">{entry.reason || '—'}</td>
                <td className="py-2 pr-4">{formatDate(entry.bannedAt)}</td>
                <td className="py-2 pr-4">
                  <button
                    onClick={() => handleRemove(entry.ip)}
                    className="flex items-center gap-1 text-green-400 hover:text-green-300"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Unban
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

const AdminPage = () => {
  const { user, logout } = useAuthStore()
  const [activeTab, setActiveTab] = useState('users')

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/lobby"
              className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Lobby
            </Link>
            <div className="flex items-center gap-2 text-white">
              <ShieldAlert className="w-6 h-6 text-red-400" />
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>{user?.username}</span>
            <button onClick={logout} className="flex items-center gap-1 hover:text-white">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        <div className="glass-effect rounded-xl p-4 md:p-6">
          <div className="flex gap-2 mb-6 p-1 bg-gray-800 rounded-lg w-fit">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {activeTab === 'users' && <UsersTab key="users" />}
          {activeTab === 'activity' && <ActivityTab key="activity" />}
          {activeTab === 'banned-ips' && <BannedIpsTab key="banned-ips" />}
        </div>
      </div>
    </div>
  )
}

export default AdminPage
